import React from 'react'
import { useSelector } from 'react-redux'
import { Navigate, useLocation } from 'react-router-dom'

import type { RouteGuardProps } from '../routes.types'
import { AppRoutes } from '../routes.types'

import { FullScreenLoader } from '@/components/common/FullScreenLoader'
import type { RootState } from '@/store/store'

export function AuthGuard({ children, requireAuth = true }: Pick<RouteGuardProps, 'children' | 'requireAuth'>) {
    const { user, isLoading, isMockAuth } = useSelector((state: RootState) => state.auth)
    const location = useLocation()

    // Check if skipAuth is in URL - allow AuthInitializer to handle mock auth setup
    const urlParams = new URLSearchParams(location.search)
    const skipAuthRequested = urlParams.get('skipAuth') === 'true'

    // Auth routes that should redirect authenticated users to dashboard
    const authRoutes = [
        AppRoutes.LOGIN,
        AppRoutes.SIGNUP,
        AppRoutes.FORGOT_PASSWORD,
        AppRoutes.RESET_PASSWORD,
        AppRoutes.VERIFY_EMAIL,
        AppRoutes.COMPLETE,
        AppRoutes.SETUP,
        AppRoutes.SIGNIN,
        AppRoutes.INVITE,
        AppRoutes.MAGIC_LINK_SENT,
    ]

    // Show loading while checking auth state (unless skipAuth is requested)
    if (isLoading && !skipAuthRequested) {
        return <FullScreenLoader text="Checking authentication..." noBackground />
    }

    // For routes that don't require auth, check if authenticated user should be redirected
    if (!requireAuth) {
        // If user is authenticated and trying to access auth routes, redirect to dashboard
        if (user && authRoutes.includes(location.pathname as AppRoutes)) {
            return <Navigate to={AppRoutes.DASHBOARD} replace />
        }
        // Otherwise, allow access to public routes (error pages, surveys, etc.)
        return <>{children}</>
    }

    // For routes that require auth:
    // - Allow access if skipAuth is requested (AuthInitializer will set mock user)
    // - Allow access if already using mock auth
    // - Otherwise redirect unauthenticated users to login
    if (requireAuth && !user && !skipAuthRequested && !isMockAuth) {
        return <Navigate to={AppRoutes.LOGIN} replace />
    }

    return <>{children}</>
}
